import { toCamel } from '@/components/utils/core/word';
import { SEARCH_CATEGORY_SLUGS_MAP, CONTENT_CATEGORY_SLUGS } from '@/components/common/UniversalSearch/constants';
import { SearchResultCategory } from '@/types/graphql';
import type { UniversalSearchResultItem } from '@/components/common/UniversalSearch/types';
import type { UserAuthor } from '@/types/graphql';

type ItemData = {
  id: string;
  slug: string;
  category: string;
  link: string;
  isExternal?: boolean;
  section?: SearchResultCategory;
};

export const buildSearchItemData = (item: UniversalSearchResultItem): ItemData | undefined => {
  switch (item.__typename) {
    case 'AcademyArticleSearchResult':
      return {
        id: String(item.article.id),
        slug: item.article.slug,
        category: item.category,
        link: `/academy/${item.article.slug}`,
        section: SearchResultCategory.AcademyArticles,
      };
    case 'CompanySearchResult':
      return {
        id: item.company.id,
        slug: item.company.slug ?? '',
        category: item.category,
        link: `/salaries/company/${item.company.slug}/`,
        isExternal: true,
        section: SearchResultCategory.Companies,
      };
    case 'PortfolioUserSearchResult':
      return {
        id: item.portfolioUser.slug,
        slug: item.portfolioUser.slug,
        category: item.category,
        link: `/p/${item.portfolioUser.slug}`,
        section: SearchResultCategory.UserPortfolios,
      };
    case 'WarRoomPostSearchResult':
      return {
        id: item.post.id,
        slug: item.post.slug,
        category: item.category,
        link: `/war-room/posts/${item.post.slug}`,
        section: SearchResultCategory.WrPosts,
      };
    case 'WarRoomUserSearchResult':
      return {
        id: String(item.warRoomUser?.id),
        slug: String(item.warRoomUser?.username),
        category: item.category,
        link: `/war-room/profile/${item.warRoomUser?.username}`,
        section: SearchResultCategory.WrUsers,
      };
    case 'WarRoomPostCommentSearchResult':
      return {
        id: String(item.record?.id),
        slug: String(item.record?.id),
        category: item.category,
        link: commentLink(item.record?.post?.slug, item.record?.id),
        section: SearchResultCategory.WrComments,
      };
  }
};

export const commentLink = (slug: string, id: string) => `/war-room/posts/${slug}/comments/${id}`;

export const displayCompanyEmployees = (value: string) =>
  value
    .split('-')
    .map(count => {
      if (count.length >= 4) {
        // Transform 1000 to 1k and 1000+ to 1k+
        return count.endsWith('+') ? `${count.slice(0, -4)}k+` : `${count.slice(0, -3)}k`;
      }
      return count;
    })
    .join('-');

export const getAuthorDetails = (author: UserAuthor) => {
  let initials = '';
  let username = '';
  let avatar = '';

  if (author.__typename === 'Public') {
    initials = author.firstname?.substring(0, 2) ?? '';
    avatar = String(author.avatarUrl);
    username = `${author.firstname} ${author.lastname}`.trim();
  } else if (author.__typename === 'Anonymous') {
    initials = author.username.substring(0, 2) ?? '';
    avatar = String(author.warRoomAvatarUrl);
    username = author.username;
  }

  return { initials, username, avatar };
};

export const getSearchCategoryBySlug = (slug: string) => {
  for (let [key, value] of SEARCH_CATEGORY_SLUGS_MAP.entries()) {
    if (slug === value) {
      return CONTENT_CATEGORY_SLUGS.includes(key) ? SearchResultCategory.Content : key;
    }
  }
};

export const getSlugBySearchCategory = (category: SearchResultCategory) => SEARCH_CATEGORY_SLUGS_MAP.get(category);

export const categoryToPaginationKey = (category: string): string => toCamel(category.toLowerCase());
